import { Link } from 'react-router-dom';
import { formatDistance } from '@wineryxplorer/shared';
import type { DistanceLocale } from '@wineryxplorer/shared';
import { CompactVenueCard } from '../SearchPage/CompactVenueCard';
import type { CompactVenueCardProps } from '../SearchPage/CompactVenueCard';

type EmptyKind =
  | { kind: 'no-location' }
  | { kind: 'no-wineries'; onWiden: () => void; widenRadiusMeters: number }
  | { kind: 'wineries-without-tastings'; venues: CompactVenueCardProps['venue'][]; userLocation?: { lat: number; lng: number } | null }
  | { kind: 'filters-too-restrictive'; onReset: () => void };

interface ExperiencesEmptyStateProps {
  state: EmptyKind;
  distanceLocale?: DistanceLocale;
  onUseMyLocation?: () => void;
}

export const ExperiencesEmptyState = ({ state, distanceLocale = 'imperial', onUseMyLocation }: ExperiencesEmptyStateProps) => {
  if (state.kind === 'no-location') {
    return (
      <div className="px-3 sm:px-0 py-16 text-center">
        <p className="font-serif text-xl text-gray-900 mb-3">Set your location to find tastings near you.</p>
        {onUseMyLocation && (
          <button
            onClick={onUseMyLocation}
            className="inline-flex items-center px-4 py-2 rounded-md bg-primary-700 text-white text-sm font-sans tracking-wide hover:bg-primary-800 transition-colors"
          >
            Use my location
          </button>
        )}
      </div>
    );
  }

  if (state.kind === 'no-wineries') {
    const widen = formatDistance(state.widenRadiusMeters, distanceLocale);
    return (
      <div className="px-3 sm:px-0 py-16 text-center">
        <p className="font-serif text-xl text-gray-900 mb-3">No tastings found in this area.</p>
        <button
          onClick={state.onWiden}
          className="inline-flex items-center px-4 py-2 rounded-md border border-gray-400 text-gray-700 text-sm font-sans tracking-wide hover:bg-gray-50 transition-colors"
        >
          Try expanding to {widen.display}
        </button>
      </div>
    );
  }

  if (state.kind === 'filters-too-restrictive') {
    return (
      <div className="px-3 sm:px-0 py-16 text-center">
        <p className="font-serif text-xl text-gray-900 mb-3">No tastings match these filters.</p>
        <button
          onClick={state.onReset}
          className="inline-flex items-center px-4 py-2 rounded-md border border-gray-400 text-gray-700 text-sm font-sans tracking-wide hover:bg-gray-50 transition-colors"
        >
          Reset filters
        </button>
      </div>
    );
  }

  // wineries-without-tastings
  return (
    <div className="px-3 sm:px-0">
      <div className="py-8 text-center max-w-2xl mx-auto">
        <p className="font-serif text-xl text-gray-900 mb-3">
          We don&apos;t have tastings listed for the wineries in this area.
        </p>
        <p className="font-sans text-sm text-gray-600 mb-2">
          Contact the winery directly for tasting options.
        </p>
        <p className="font-sans text-sm text-gray-600">
          If you run a winery,{' '}
          <Link to="/login?signup=venue-admin" className="text-secondary-700 underline hover:text-secondary-800">
            sign up to list your tastings
          </Link>
          .
        </p>
      </div>

      <h2 className="font-serif text-xl font-normal text-gray-900 mt-8 mb-4 px-3 sm:px-0">
        Wineries in this area
      </h2>
      <ul className="space-y-3">
        {state.venues.map((v) => (
          <li key={v._id}>
            <CompactVenueCard venue={v} userLocation={state.userLocation ?? null} distanceLocale={distanceLocale} />
          </li>
        ))}
      </ul>
    </div>
  );
};
